/**
 * `useLockAndTransmit` — wraps the middleware lock endpoint
 *   POST /visits/:encounterId/lock
 * which:
 *   - Flips the Progress Note `DocumentReference.docStatus` from
 *     `preliminary` to `final`.
 *   - Writes an attestation Provenance (activity = AU).
 *   - Sets the linked Appointment.status to `fulfilled`.
 *
 * Idempotent: re-locking a `final` doc returns `alreadyLocked: true` and
 * echoes the original `attestedAt`. Callers surface this as a soft
 * notification rather than an error.
 *
 * Phase 6.4 — the hook hardcodes `attested: true` internally. The UI no
 * longer gates this on a checkbox (LD-3 + LD-11 removed the "I attest"
 * scaffolding); the Lock click itself is the signature per HL7
 * (`Provenance.activity = 'AU'`). The backend DTO still requires the
 * boolean to be `true` — we set it once here so callers don't have to
 * thread it through.
 *
 * Cache invalidation:
 *   - TanStack queries: DocumentReference (the viewer re-fetches the doc so the
 *     new `docStatus = 'final'` shows + the "Lock Note" button hides), the
 *     Provenance (so the attestation badge appears), and PatientChart counts.
 *   - `medplum.invalidateAll()`: Lock MATERIALIZES the whole clinical cascade,
 *     which the chart reads through the Medplum SDK cache — the TanStack
 *     invalidations don't reach it, so the SDK cache is cleared too. Without
 *     this the chart sections show stale/empty until a hard refresh.
 *
 * Returns the TanStack Query mutation surface verbatim — consumers use
 * `mutate`, `isPending`, `error`, `data`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

// The statuses the lock endpoint refuses with: BAD_REQUEST, FORBIDDEN, NOT_FOUND,
// CONFLICT. A 401 is a session the client could not recover (one retry,
// `orpc/client.ts`) and a 408 or 429 is transient; neither is a refusal.
const REFUSAL_STATUSES = new Set([400, 403, 404, 409])

export type LockAndTransmitResponse = Awaited<ReturnType<OrpcClient['visitLockProgressNote']>>

export type LockAndTransmitInput = {
  encounterId: string
  documentReferenceId: string
}

export function useLockAndTransmit() {
  const client = useClient()
  const queryClient = useQueryClient()
  const medplum = useMedplum()

  return useMutation<LockAndTransmitResponse, unknown, LockAndTransmitInput>({
    mutationFn: async ({ encounterId, documentReferenceId }) => {
      // Option A — the backend MATERIALIZES the clinical resources from the
      // lossless draft blob it already stored on the preliminary
      // DocumentReference at save time. The FE no longer reconstructs the note
      // from the lossy QuestionnaireResponse, so Lock is a clean no-payload
      // call: just attest + reference the doc. (The legacy client `soapNote`
      // field was dropped from the lock DTO — the server materializes from the
      // stored draft blob it saved, never from the request body.)
      return client.visitLockProgressNote({
        encounterId,
        documentReferenceId,
        // Backend DTO still requires this flag — set it once here. The UI no
        // longer exposes a checkbox (LD-3); the Lock click IS the signature
        // (`Provenance.activity = 'AU'`).
        attested: true,
      })
    },
    onSuccess: async (data) => {
      // The viewer + chart timeline both fetch DocumentReference; the
      // attestation Provenance also needs to refresh so the "Locked &
      // TEFCA-queued" badge appears.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['DocumentReference'] }),
        queryClient.invalidateQueries({ queryKey: ['Provenance'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chart-counts'] }),
      ])
      // Lock materializes the full clinical cascade (Conditions, Meds, Vitals,
      // Procedures, Orders, Claim) + flips the DocRef + writes Provenance. The
      // chart reads those through the Medplum SDK cache, which the TanStack
      // invalidations above do NOT reach — clear it so the chart refreshes.
      medplum.invalidateAll()

      if (data.alreadyLocked) {
        showNotification({
          color: 'blue',
          title: 'Already locked',
          message: 'This note was already attested.',
        })
        return
      }

      showNotification({
        color: 'green',
        title: 'Note locked',
        message: 'Progress note attested and queued for TEFCA transmission.',
      })
    },
    onError: (err) => {
      logError('progress-note-viewer', 'Lock and transmit failed', err)
      // A refusal carries a reason the doctor can act on (a $0 or
      // credential-refused line, unreviewed vitals, a locked sibling) and is
      // shown verbatim; anything else keeps the retry prompt. Read duck-typed:
      // `instanceof ORPCError` does not survive bundle boundaries.
      const status = (err as { status?: number } | null)?.status
      const refused = typeof status === 'number' && REFUSAL_STATUSES.has(status)
      showNotification({
        color: 'red',
        title: refused ? 'Lock refused' : 'Lock failed',
        message:
          refused && err instanceof Error && err.message
            ? err.message
            : 'Could not lock the progress note. Please try again.',
      })
    },
  })
}
